/**
 * Home — phase 2 §7.2.
 *
 * "**The three existing queues collapse into Home.** 'Reviews due', 'Review
 * waiting on you', and 'Acknowledgments' are already person-scoped and already
 * built; they are sections of a landing page, not three navigation entries.
 * Three queues in a sidebar is the console pattern again."
 *
 * So this page owns no data of its own. Each section is a count and the first
 * few rows of a list that already has a screen, and every one of them links to
 * that screen rather than reimplementing it — the queues stay the authority on
 * their own contents, and Home is where a person finds out any of them has
 * something in it.
 *
 * **Counts are never derived from a truncated page.** Each section shows the
 * total the API reported and renders a handful of rows beneath it, because a
 * landing page that said "3 waiting" from `rows.length` would start lying the
 * moment a queue outgrew its preview.
 *
 * **What is not here: a cross-project activity feed.** §7.2 asks Home for
 * "recent activity on your projects" as well, and the only activity query that
 * exists is per project (`GET /api/projects/:id/activity`, §4.3). Rather than
 * assemble one client-side out of several — which is the shape §7.5 warns
 * about — the projects section lists the projects a person can reach and each
 * row opens the page that already carries its timeline.
 */

import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import {
  nodeQuery,
  useApprovalsAwaitingMe,
  useMe,
  useOwedAcknowledgments,
  useProjects,
  useReviewDue,
  useUnreadCount,
} from '../api/queries.ts';
import { Badge, roleLabel } from '../components/ui/badge.tsx';
import { Loading } from '../components/ui/feedback.tsx';
import { Card, PageHeader } from '../components/ui/layout.tsx';
import { formatCalendarDate } from './documents/review.tsx';

/**
 * One queue.
 *
 * The empty state is a sentence rather than a blank card, and it is the sentence
 * the queue itself would give: "nothing is waiting on you" is a real answer and
 * a section that disappeared when empty would read as one that failed to load.
 */
function Queue({
  title,
  href,
  total,
  pending,
  empty,
  children,
}: {
  title: string;
  href: ReactNode;
  total: number | undefined;
  pending: boolean;
  empty: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-md font-semibold text-primary">{title}</h2>
        {typeof total === 'number' && total > 0 ? <Badge tone="brand">{total}</Badge> : null}
      </div>
      <div className="mt-3 flex flex-col gap-2 text-sm text-secondary">
        {pending ? <Loading label="Loading…" /> : null}
        {!pending && total === 0 ? <p>{empty}</p> : null}
        {children}
      </div>
      <div className="mt-4 border-t border-secondary pt-3 text-sm">{href}</div>
    </Card>
  );
}

function Row({ to, children }: { to: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="min-w-0 truncate">{to}</span>
      {children}
    </div>
  );
}

const PREVIEW = 4;

export function HomePage() {
  const me = useMe();
  const approvals = useApprovalsAwaitingMe();
  const acknowledgments = useOwedAcknowledgments();
  const reviews = useReviewDue('mine');
  const unread = useUnreadCount();
  const projects = useProjects({ status: 'active' });

  const user = me.data?.user;
  const requests = approvals.data?.requests ?? [];
  const owed = acknowledgments.data?.acknowledgments ?? [];
  const due = reviews.data?.documents ?? [];
  const rows = projects.data?.projects ?? [];

  // An approval request names its subject by id, so the titles come from the
  // node cache — the same fetch the review queue itself makes, and the reason
  // this list can say "Client Agreement Policy" rather than a uuid.
  const titles = useQueries({
    queries: requests.map((request) => nodeQuery(request.subject.id)),
    combine: (results) =>
      new Map(
        results.flatMap((result) =>
          result.data ? [[result.data.node.id, result.data.node.title] as const] : [],
        ),
      ),
  });

  return (
    <>
      <PageHeader
        title={user ? `Hello, ${user.name.split(' ')[0]}` : 'Home'}
        description="What is waiting on you, and the projects you can reach. Every section below is a queue with its own screen — this is where you find out one of them has something in it."
        actions={
          user ? (
            <span className="flex items-center gap-2">
              <Badge tone="brand">{roleLabel(user.role)}</Badge>
              {unread.data && unread.data.unreadCount > 0 ? (
                <Link to="/notifications" className="text-sm link">
                  {unread.data.unreadCount} unread
                </Link>
              ) : null}
            </span>
          ) : null
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Queue
          title="Review waiting on you"
          total={approvals.data?.total}
          pending={approvals.isPending}
          empty="Nobody is waiting on a decision from you."
          href={
            <Link to="/approvals" className="link">
              Open the review queue
            </Link>
          }
        >
          {requests.slice(0, PREVIEW).map((request) => (
            <Row
              key={request.id}
              to={
                <Link to="/approvals/$requestId" params={{ requestId: request.id }}>
                  {titles.get(request.subject.id) ?? 'Untitled document'}
                </Link>
              }
            />
          ))}
        </Queue>

        <Queue
          title="Acknowledgments"
          total={acknowledgments.data?.total}
          pending={acknowledgments.isPending}
          empty="You do not owe a read of anything."
          href={
            <Link to="/acknowledgments" className="link">
              Open what you owe
            </Link>
          }
        >
          {owed.slice(0, PREVIEW).map((cycle) => (
            <Row
              key={cycle.requestId}
              to={
                <Link to="/documents/$nodeId" params={{ nodeId: cycle.documentId }}>
                  {cycle.title}
                </Link>
              }
            >
              {cycle.dueDate ? (
                <span
                  className={cycle.overdue ? 'text-xs text-error-primary' : 'text-xs text-tertiary'}
                >
                  {formatCalendarDate(cycle.dueDate)}
                </span>
              ) : null}
            </Row>
          ))}
        </Queue>

        <Queue
          title="Reviews due"
          total={reviews.data?.total}
          pending={reviews.isPending}
          empty="Nothing you own is past its review date."
          href={
            <Link to="/documents/review" className="link">
              Open the past-review list
            </Link>
          }
        >
          {due.slice(0, PREVIEW).map((document) => (
            <Row
              key={document.nodeId}
              to={
                <Link to="/documents/$nodeId" params={{ nodeId: document.nodeId }}>
                  {document.title}
                </Link>
              }
            >
              {document.reviewBy ? (
                <span className="text-xs text-tertiary">
                  {formatCalendarDate(document.reviewBy)}
                </span>
              ) : null}
            </Row>
          ))}
        </Queue>
      </div>

      <Card>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-md font-semibold text-primary">Your projects</h2>
          <Link to="/projects" className="text-sm link">
            All projects
          </Link>
        </div>
        <p className="mt-1 text-sm text-tertiary">
          Active work you can reach. A project page carries its own recent activity, drawn from the
          event log — which is where “what happened lately” lives rather than here.
        </p>
        <div className="mt-3 flex flex-col gap-2 text-sm">
          {projects.isPending ? <Loading label="Loading projects…" /> : null}
          {!projects.isPending && rows.length === 0 ? (
            <p className="text-secondary">
              No active project names you yet. Access is a grant on the project’s space, written by
              somebody at kickoff.
            </p>
          ) : null}
          {rows.map((project) => (
            <Row
              key={project.id}
              to={
                <Link to="/projects/$projectId" params={{ projectId: project.id }}>
                  {project.name}
                </Link>
              }
            >
              <span className="text-xs text-tertiary">{project.organization.name}</span>
            </Row>
          ))}
        </div>
      </Card>
    </>
  );
}
